import Sentry from "@similarweb/sentry-logger";

import { QuizSplitUseCaseGroup } from "app/pages/activation/components/FirstLandModalProvider/constants";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { FirstLandRoutes } from "./constants";
import type { LandingParams, LandingPlan, LandingAction } from "./types";

export const NAV_REPLACE = { location: "replace" } as const;

export function inBrowser(): boolean {
    return typeof window !== "undefined";
}

export function safeSessionSet(key: string, value: string): void {
    if (!inBrowser()) {
        return;
    }

    try {
        window.sessionStorage?.setItem(key, value);
    } catch (error) {
        Sentry.errorTracker({
            message: "Error when setting to sessionStorage",
            e: error,
            level: "error",
        });
    }
}

export function safeSessionRemove(key: string): void {
    if (!inBrowser()) {
        return;
    }

    try {
        window.sessionStorage?.removeItem(key);
    } catch (error) {
        Sentry.errorTracker({
            message: "Error when removing from sessionStorage",
            e: error,
            level: "error",
        });
    }
}

export function safeSessionGetBoolean(key: string): boolean | undefined {
    if (!inBrowser()) {
        return undefined;
    }
    try {
        const val = window.sessionStorage?.getItem(key);
        if (val === "true") {
            return true;
        }
        if (val === "false") {
            return false;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export function cleanUrlParams(
    urlParams: URLSearchParams,
    toDelete: string[],
    replaceUrl?: (cleanUrl: URL) => void,
): void {
    toDelete.forEach((p) => urlParams.delete(p));

    if (!inBrowser()) {
        return;
    }

    try {
        const current = new URL(window.location.href);
        const nextSearch = urlParams.toString();
        current.search = nextSearch ? `?${nextSearch}` : "";
        if (typeof replaceUrl === "function") {
            replaceUrl(current);
        } else {
            try {
                window.history.replaceState({}, "", current);
            } catch {
                window.location.replace(current.toString());
            }
        }
    } catch (error) {
        Sentry.errorTracker({
            message: "Error in cleanUrlParams function, when removing unneeded url params",
            e: error,
            level: "error",
        });
    }
}

export const goReplace = (state: FirstLandRoutes, params?: LandingParams) =>
    proNavigator.go(state, params, NAV_REPLACE);

export const isBlank = (value?: string) => value?.trim().length === 0;

export const createLandingPlan = (variant: "default" | "business"): LandingPlan => {
    const isDefault = variant === "default";

    return {
        [QuizSplitUseCaseGroup.ANALYZE_WEBSITES]: ({ params }) =>
            isDefault
                ? goReplace(FirstLandRoutes.WebsiteAnalysisHome)
                : goReplace(FirstLandRoutes.WebsiteAnalysisWSPerformance, params),
        [QuizSplitUseCaseGroup.IMPROVE_SEO]: ({ isCiPackage, params }) => {
            if (isCiPackage) {
                return goReplace(FirstLandRoutes.WebsiteAnalysisSearch, params);
            }

            if (isDefault) {
                return goReplace(FirstLandRoutes.OrganicSearchHome);
            }

            return goReplace(FirstLandRoutes.OrganicSearchSeo, params);
        },
        [QuizSplitUseCaseGroup.GEN_AI]: ({ params }) => {
            return goReplace(FirstLandRoutes.AiBrandVisibility, params);
        },
        fallback: () =>
            isDefault
                ? goReplace(FirstLandRoutes.WebsiteAnalysisHome)
                : goReplace(FirstLandRoutes.WebsiteAnalysisWSPerformance),
    };
};

export const DEFAULT_PLAN = createLandingPlan("default");

export const BUSINESS_PLAN = createLandingPlan("business");

export const resolveByPlan = (plan: LandingPlan, quiz: QuizSplitUseCaseGroup, args: Parameters<LandingAction>[0]) =>
    (plan[quiz] ?? plan.fallback)(args);

export const getDefaultLandingState = (quiz: QuizSplitUseCaseGroup, isCiPackage?: boolean, params?: LandingParams) =>
    resolveByPlan(DEFAULT_PLAN, quiz, { isCiPackage, params });

export const getBusinessLandingState = (quiz: QuizSplitUseCaseGroup, isCiPackage?: boolean, params?: LandingParams) =>
    resolveByPlan(BUSINESS_PLAN, quiz, { isCiPackage, params });

export const quizEmailToDomain = (email: string): string => {
    const trimmed = email.trim();
    const at = trimmed.lastIndexOf("@");

    return at >= 0 ? trimmed.slice(at + 1).trim() : "";
};
